import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { RadioGroup, RadioGroupItem } from "../components/ui/radio-group";
import { Alert, AlertDescription } from "../components/ui/alert";
import { Building2, Briefcase, Loader2 } from "lucide-react";
import { formatApiError } from "../lib/api";
import { toast } from "sonner";

export default function Login() {
  const navigate = useNavigate();
  const { user, loading: authLoading, login, register } = useAuth();
  const [tab, setTab] = useState("login");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [company, setCompany] = useState("");
  const [role, setRole] = useState("buyer");

  const goByRole = (r) => navigate(r === "vendor" ? "/vendor-dashboard" : "/dashboard");

  useEffect(() => {
    if (!authLoading && user) goByRole(user.role);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authLoading, user]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setError(""); setLoading(true);
    try {
      const u = await login(email, password);
      toast.success("Welcome back!");
      goByRole(u.role);
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally { setLoading(false); }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    setError(""); setLoading(true);
    try {
      const u = await register({ email, password, name, role, company });
      toast.success("Account created");
      goByRole(u.role);
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally { setLoading(false); }
  };

  const fillDemo = (kind) => {
    setTab("login");
    if (kind === "buyer") { setEmail("buyer@acmecorp.com"); setPassword("demo1234"); }
    else { setEmail("vendor@techserve.in"); setPassword("demo1234"); }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-[#FAFAFA]">
      {/* Left brand panel */}
      <div className="hidden lg:flex flex-col justify-between p-12 bg-[#0F172A] text-white relative overflow-hidden">
        <div className="absolute inset-0 opacity-30" style={{
          backgroundImage: "radial-gradient(circle at 20% 30%, rgba(99,102,241,0.3) 0%, transparent 40%), radial-gradient(circle at 80% 70%, rgba(59,130,246,0.2) 0%, transparent 40%)"
        }} />
        <div className="relative z-10 flex items-center gap-2">
          <div className="w-10 h-10 rounded-md bg-[#6366F1] grid place-items-center font-bold text-xl font-outfit">V</div>
          <span className="font-outfit font-bold text-2xl">VendorHub</span>
        </div>
        <div className="relative z-10 space-y-6">
          <div className="text-[11px] uppercase tracking-widest text-indigo-300 font-semibold">Enterprise Procurement OS</div>
          <h1 className="text-5xl font-bold font-outfit leading-tight tracking-tight">
            One platform.<br />Every supplier.<br /><span className="text-[#818CF8]">Zero spreadsheets.</span>
          </h1>
          <p className="text-zinc-400 text-base max-w-md">
            Discover, evaluate, onboard, and manage your vendor ecosystem with the rigor your procurement team deserves.
          </p>
          <div className="grid grid-cols-3 gap-6 pt-4 border-t border-white/10">
            <div><div className="text-2xl font-bold font-outfit">1,200+</div><div className="text-xs text-zinc-500 mt-1">Verified Vendors</div></div>
            <div><div className="text-2xl font-bold font-outfit">₹840Cr</div><div className="text-xs text-zinc-500 mt-1">Spend Managed</div></div>
            <div><div className="text-2xl font-bold font-outfit">99.2%</div><div className="text-xs text-zinc-500 mt-1">SLA Adherence</div></div>
          </div>
        </div>
        <div className="relative z-10 text-xs text-zinc-500">© 2026 VendorHub · Built for the procurement teams of tomorrow</div>
      </div>

      {/* Right form panel */}
      <div className="flex items-center justify-center p-6 lg:p-12">
        <div className="w-full max-w-md">
          <div className="lg:hidden flex items-center gap-2 mb-8 justify-center">
            <div className="w-9 h-9 rounded-md bg-[#0F172A] grid place-items-center text-white font-bold font-outfit">V</div>
            <span className="font-outfit font-bold text-xl">VendorHub</span>
          </div>

          <Tabs value={tab} onValueChange={setTab} className="w-full">
            <TabsList className="grid grid-cols-2 mb-6">
              <TabsTrigger value="login" data-testid="tab-login">Sign in</TabsTrigger>
              <TabsTrigger value="signup" data-testid="tab-signup">Create account</TabsTrigger>
            </TabsList>

            <TabsContent value="login">
              <h2 className="text-2xl font-bold font-outfit mb-1">Welcome back</h2>
              <p className="text-sm text-zinc-500 mb-6">Sign in to your VendorHub workspace</p>

              <form onSubmit={handleLogin} className="space-y-4">
                <div>
                  <Label htmlFor="email">Email</Label>
                  <Input id="email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required data-testid="login-email-input" className="mt-1.5" />
                </div>
                <div>
                  <Label htmlFor="password">Password</Label>
                  <Input id="password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} required data-testid="login-password-input" className="mt-1.5" />
                </div>
                {error && <Alert variant="destructive"><AlertDescription>{error}</AlertDescription></Alert>}
                <Button type="submit" className="w-full bg-[#6366F1] hover:bg-[#4F46E5]" disabled={loading} data-testid="login-submit-button">
                  {loading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />} Sign in
                </Button>
              </form>

              <div className="mt-8 border-t border-zinc-200 pt-6">
                <div className="text-xs uppercase tracking-widest text-zinc-500 mb-3 font-semibold">Demo accounts</div>
                <div className="grid grid-cols-2 gap-2">
                  <button type="button" onClick={() => fillDemo("buyer")} className="text-left p-3 border border-zinc-200 hover:border-[#6366F1] rounded-md transition-colors" data-testid="demo-buyer-btn">
                    <div className="flex items-center gap-2 text-xs font-semibold text-zinc-700"><Briefcase className="w-3 h-3" /> Buyer</div>
                    <div className="text-[11px] text-zinc-500 mt-1 font-mono">buyer@acmecorp.com</div>
                  </button>
                  <button type="button" onClick={() => fillDemo("vendor")} className="text-left p-3 border border-zinc-200 hover:border-[#6366F1] rounded-md transition-colors" data-testid="demo-vendor-btn">
                    <div className="flex items-center gap-2 text-xs font-semibold text-zinc-700"><Building2 className="w-3 h-3" /> Vendor</div>
                    <div className="text-[11px] text-zinc-500 mt-1 font-mono">vendor@techserve.in</div>
                  </button>
                </div>
                <div className="text-[11px] text-zinc-400 mt-2">Password: <span className="font-mono">demo1234</span></div>
              </div>
            </TabsContent>

            <TabsContent value="signup">
              <h2 className="text-2xl font-bold font-outfit mb-1">Get started</h2>
              <p className="text-sm text-zinc-500 mb-6">Choose your role and create your account</p>

              <form onSubmit={handleRegister} className="space-y-4">
                <div>
                  <Label className="mb-2 block">I am a...</Label>
                  <RadioGroup value={role} onValueChange={setRole} className="grid grid-cols-2 gap-2">
                    <label className={`flex items-start gap-3 p-3 border rounded-md cursor-pointer transition-all ${role === "buyer" ? "border-[#6366F1] bg-indigo-50/50" : "border-zinc-200"}`} data-testid="role-buyer">
                      <RadioGroupItem value="buyer" className="mt-0.5" />
                      <div>
                        <div className="text-sm font-semibold flex items-center gap-1"><Briefcase className="w-3 h-3" /> Buyer</div>
                        <div className="text-xs text-zinc-500 mt-0.5">Procurement team</div>
                      </div>
                    </label>
                    <label className={`flex items-start gap-3 p-3 border rounded-md cursor-pointer transition-all ${role === "vendor" ? "border-[#6366F1] bg-indigo-50/50" : "border-zinc-200"}`} data-testid="role-vendor">
                      <RadioGroupItem value="vendor" className="mt-0.5" />
                      <div>
                        <div className="text-sm font-semibold flex items-center gap-1"><Building2 className="w-3 h-3" /> Vendor</div>
                        <div className="text-xs text-zinc-500 mt-0.5">Supplier company</div>
                      </div>
                    </label>
                  </RadioGroup>
                </div>
                <div>
                  <Label htmlFor="name">Full name</Label>
                  <Input id="name" value={name} onChange={(e) => setName(e.target.value)} required data-testid="signup-name-input" className="mt-1.5" />
                </div>
                <div>
                  <Label htmlFor="company">Company</Label>
                  <Input id="company" value={company} onChange={(e) => setCompany(e.target.value)} required data-testid="signup-company-input" className="mt-1.5" />
                </div>
                <div>
                  <Label htmlFor="signup-email">Work email</Label>
                  <Input id="signup-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required data-testid="signup-email-input" className="mt-1.5" />
                </div>
                <div>
                  <Label htmlFor="signup-password">Password</Label>
                  <Input id="signup-password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} required minLength={6} data-testid="signup-password-input" className="mt-1.5" />
                </div>
                {error && <Alert variant="destructive"><AlertDescription>{error}</AlertDescription></Alert>}
                <Button type="submit" className="w-full bg-[#6366F1] hover:bg-[#4F46E5]" disabled={loading} data-testid="signup-submit-button">
                  {loading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />} Create account
                </Button>
              </form>
            </TabsContent>
          </Tabs>
        </div>
      </div>
    </div>
  );
}
